import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, AlertTriangle, LogOut } from "lucide-react";
import UnifiedPortalHeader from "@/components/UnifiedPortalHeader";
import SharedFooter from "@/components/SharedFooter";

import {
  TestConfigView,
  ActiveTestView,
  TestResultsView,
  TestHomeView,
  FeatureLockedView
} from "@/components/student/practice";

export default function PracticeTests() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [planType, setPlanType] = useState("free");
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  
  // Test state
  const [view, setView] = useState("home"); // home, config, test, results
  const [testConfig, setTestConfig] = useState({
    subject: "",
    numQuestions: 10,
    timeLimit: 15
  });
  const [activeTest, setActiveTest] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [testResults, setTestResults] = useState(null);
  const [testHistory, setTestHistory] = useState([]);

  // Handle browser back button - show confirmation
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      window.history.pushState(null, '', window.location.href);
      setShowBackConfirm(true);
    };

    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include"
      });
      navigate("/");
    } catch (error) {
      navigate("/");
    }
  };

  // Fetch user data and access
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Check access
        const accessRes = await fetch(`${API}/learn/practice-tests/access`, {
          credentials: "include"
        });
        const accessData = await accessRes.json();
        
        if (accessData.success) {
          setHasAccess(accessData.has_access);
          setUpgradeMessage(accessData.message);
          setPlanType(accessData.plan_type || "free");
          setUser(accessData.student);
        }

        // Fetch history if has access
        if (accessData.has_access) {
          const historyRes = await fetch(`${API}/learn/practice-tests/history`, {
            credentials: "include"
          });
          const historyData = await historyRes.json();
          if (historyData.success) {
            setTestHistory(historyData.tests || []);
          }
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Failed to load practice tests");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Timer effect
  useEffect(() => {
    let interval;
    if (view === "test" && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            finishTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, timeRemaining]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const startTest = async () => {
    if (!testConfig.subject) {
      toast.error("Please select a subject");
      return;
    }

    try {
      const response = await fetch(`${API}/learn/practice-tests/start`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: testConfig.subject,
          num_questions: testConfig.numQuestions,
          time_limit_minutes: testConfig.timeLimit
        })
      });

      const data = await response.json();

      if (data.success) {
        setActiveTest(data.test);
        setCurrentQuestion(0);
        setAnswers({});
        setTimeRemaining(testConfig.timeLimit * 60);
        setView("test");
      } else {
        toast.error(data.message || "Failed to start test");
      }
    } catch (error) {
      toast.error("Failed to start test");
    }
  };

  const submitAnswer = async (answer) => {
    const newAnswers = { ...answers, [currentQuestion]: answer };
    setAnswers(newAnswers);

    if (currentQuestion < activeTest.num_questions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      finishTest(newAnswers);
    }
  };

  const finishTest = useCallback(async (finalAnswers = answers) => {
    try {
      const answersArray = Object.keys(finalAnswers).map(idx => ({
        question_index: parseInt(idx),
        answer: finalAnswers[idx]
      }));

      const response = await fetch(`${API}/learn/practice-tests/${activeTest.id}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ answers: answersArray })
      });

      const data = await response.json();

      if (data.success) {
        setTestResults(data.results);
        setView("results");
        
        // Refresh history
        const historyRes = await fetch(`${API}/learn/practice-tests/history`, {
          credentials: "include"
        });
        const historyData = await historyRes.json();
        if (historyData.success) {
          setTestHistory(historyData.tests || []);
        }
      } else {
        toast.error("Failed to submit test");
      }
    } catch (error) {
      toast.error("Failed to submit test");
    }
  }, [answers, activeTest]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Feature locked view
  if (!hasAccess) {
    return <FeatureLockedView upgradeMessage={upgradeMessage} />;
  }

  // Test configuration view
  if (view === "config") {
    const subjects = user?.subscription?.subjects || ["Mathematics", "Science", "English"];
    
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col">
        <UnifiedPortalHeader
          portalType="student"
          userName=""
          userSubtitle=""
          onLogout={() => navigate("/student/profile")}
          showUserInfo={false}
          showHomeButton={false}
          hideLogout={true}
        />
        <main className="flex-1">
          <TestConfigView
            testConfig={testConfig}
            setTestConfig={setTestConfig}
            subjects={subjects}
            onStart={startTest}
            onBack={() => setView("home")}
          />
        </main>
        <SharedFooter />
      </div>
    );
  }

  // Active test view
  if (view === "test" && activeTest) {
    return (
      <ActiveTestView
        activeTest={activeTest}
        currentQuestion={currentQuestion}
        setCurrentQuestion={setCurrentQuestion}
        answers={answers}
        setAnswers={setAnswers}
        timeRemaining={timeRemaining}
        formatTime={formatTime}
        onSubmitAnswer={submitAnswer}
      />
    );
  }

  // Results view
  if (view === "results" && testResults) {
    return (
      <TestResultsView
        testResults={testResults}
        onBackToTests={() => {
          setView("home");
          setActiveTest(null);
          setTestResults(null);
        }}
        onTakeAnother={() => setView("config")}
      />
    );
  }

  // Home view
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <UnifiedPortalHeader
        portalType="student"
        userName=""
        userSubtitle=""
        onLogout={() => navigate("/student/profile")}
        showUserInfo={false}
        showHomeButton={false}
        hideLogout={true}
      />
      <main className="flex-1">
        <TestHomeView
          planType={planType}
          testHistory={testHistory}
          onNewTest={() => setView("config")}
        />
      </main>
      
      {/* Back Button Confirmation Dialog */}
      <Dialog open={showBackConfirm} onOpenChange={setShowBackConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-orange-500" />
            </div>
            <DialogTitle className="text-center">Leave Practice Tests?</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to leave? You&apos;ll be logged out of your session.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              onClick={() => setShowBackConfirm(false)}
              className="sm:w-auto"
            >
              Stay Here
            </Button>
            <Button
              onClick={handleLogout}
              className="bg-red-500 hover:bg-red-600 text-white sm:w-auto"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <SharedFooter />
    </div>
  );
}
